import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';
import { FocusTrap } from './util/focus-trap';
import { lock, unlock } from './util/scroll-bar';

const ANIMATION_DURATION = 200;
const EVENT_SHOW = 'antumo:modal:show';
const EVENT_SHOWN = 'antumo:modal:shown';
const EVENT_HIDE = 'antumo:modal:hide';
const EVENT_HIDDEN = 'antumo:modal:hidden';
const CLASS_CONTENT_CLOSING = 'modal-content-closing';
const CLASS_OVERLAY_CLOSING = 'modal-closing';

export class Modal extends BaseComponent {
  static get NAME() { return 'modal'; }
  static DefaultConfig: ComponentConfig = { backdrop: true, keyboard: true, focus: true };

  private _isShown = false;
  private _isTransitioning = false;
  private _triggerElement: Element | null = null;
  private _focusTrap: FocusTrap | null = null;
  private _overlay!: HTMLElement;
  private _modalElement: HTMLElement | null = null;
  private _boundHandleKeydown!: (e: KeyboardEvent) => void;
  private _boundHandleOverlayClick!: (e: MouseEvent) => void;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);

    if (this._element !== element) return;

    this._overlay = element as HTMLElement;
    this._modalElement = this._overlay.querySelector('.modal-content');
    this._boundHandleKeydown = this._handleKeydown.bind(this);
    this._boundHandleOverlayClick = this._handleOverlayClick.bind(this);

    if (this._modalElement) {
      this._focusTrap = new FocusTrap(this._modalElement);
    }
  }

  show(relatedTarget?: Element): void {
    if (this._isShown || this._isTransitioning) return;

    const showEvent = triggerEvent(this._overlay, EVENT_SHOW, { relatedTarget }, true);
    if (showEvent.defaultPrevented) return;

    this._triggerElement = relatedTarget ?? null;

    // Must be an explicit value, not ''. Clearing the inline style hands the
    // decision back to the stylesheet, where the base rule is
    // `.modal { display: none }` — so the overlay stayed hidden and no
    // data-attribute modal ever opened. _completeHide() sets 'none' the same
    // explicit way, so the two stay symmetrical.
    this._overlay.style.display = 'flex';

    if (this._modalElement) {
      if (!this._modalElement.hasAttribute('role')) {
        this._modalElement.setAttribute('role', 'dialog');
      }
      if (!this._modalElement.hasAttribute('tabindex')) {
        this._modalElement.setAttribute('tabindex', '-1');
      }
      this._modalElement.setAttribute('aria-modal', 'true');
    }

    lock();

    if (this._config.focus && this._focusTrap) {
      this._focusTrap.activate();
    }

    document.addEventListener('keydown', this._boundHandleKeydown);
    this._overlay.addEventListener('click', this._boundHandleOverlayClick);

    this._isShown = true;

    triggerEvent(this._overlay, EVENT_SHOWN, { relatedTarget }, false);
  }

  hide(): void {
    if (!this._isShown || this._isTransitioning) return;

    const hideEvent = triggerEvent(this._overlay, EVENT_HIDE, {}, true);
    if (hideEvent.defaultPrevented) return;

    this._isTransitioning = true;

    if (this._modalElement) {
      this._modalElement.classList.add(CLASS_CONTENT_CLOSING);
    }
    this._overlay.classList.add(CLASS_OVERLAY_CLOSING);

    if (this._focusTrap) {
      this._focusTrap.deactivate();
    }

    const delay = this._isReducedMotion() ? 0 : ANIMATION_DURATION;

    setTimeout(() => {
      this._completeHide();
    }, delay);
  }

  dispose(): void {
    if (this._isShown) {
      this._forceHide();
    }
    this._focusTrap = null;
    this._modalElement = null;
    this._triggerElement = null;
    super.dispose();
  }

  private _completeHide(): void {
    if (this._modalElement) {
      this._modalElement.classList.remove(CLASS_CONTENT_CLOSING);
      this._modalElement.removeAttribute('aria-modal');
    }
    this._overlay.classList.remove(CLASS_OVERLAY_CLOSING);
    this._overlay.style.display = 'none';

    unlock();

    document.removeEventListener('keydown', this._boundHandleKeydown);
    this._overlay.removeEventListener('click', this._boundHandleOverlayClick);

    if (this._triggerElement && this._triggerElement instanceof HTMLElement) {
      this._triggerElement.focus();
    }

    this._isShown = false;
    this._isTransitioning = false;

    triggerEvent(this._overlay, EVENT_HIDDEN, {}, false);
  }

  private _forceHide(): void {
    if (this._focusTrap) {
      this._focusTrap.deactivate();
    }
    if (this._modalElement) {
      this._modalElement.classList.remove(CLASS_CONTENT_CLOSING);
      this._modalElement.removeAttribute('aria-modal');
    }
    this._overlay.classList.remove(CLASS_OVERLAY_CLOSING);
    this._overlay.style.display = 'none';
    unlock();
    document.removeEventListener('keydown', this._boundHandleKeydown);
    this._overlay.removeEventListener('click', this._boundHandleOverlayClick);
    this._isShown = false;
    this._isTransitioning = false;
  }

  private _handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this._config.keyboard) {
      this.hide();
    }
  }

  private _handleOverlayClick(event: MouseEvent): void {
    if (event.target === this._overlay && this._config.backdrop) {
      this.hide();
    }
  }

  private _isReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
